import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from '@/components/ui/Icon'

interface ThemedSelectOption<T extends string> {
  value: T
  label: string
}

interface ThemedSelectProps<T extends string> {
  value: T
  options: readonly ThemedSelectOption<T>[]
  onChange: (value: T) => void
  ariaLabel?: string
  className?: string
}

export function ThemedSelect<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: ThemedSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [dropUp, setDropUp] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const selected = options.find((option) => option.value === value) ?? options[0]

  // Open upward when there isn't enough room below the trigger (e.g. when the
  // select sits near the bottom of the viewport, like the sidebar footer).
  function openMenu() {
    const rect = rootRef.current?.getBoundingClientRect()
    if (rect) {
      const estimatedMenuHeight = options.length * 32 + 8
      const spaceBelow = window.innerHeight - rect.bottom
      setDropUp(spaceBelow < estimatedMenuHeight && rect.top > spaceBelow)
    }
    setIsOpen(true)
  }

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <div className={`cg-select-wrap${className ? ` ${className}` : ''}`} ref={rootRef}>
      <button
        type="button"
        className="cg-select"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        onClick={() => (isOpen ? setIsOpen(false) : openMenu())}
      >
        <span>{selected.label}</span>
        <Icon name={isOpen ? 'collapse' : 'expand'} size={11} />
      </button>

      {isOpen && (
        <div
          className={`cg-select-menu${dropUp ? ' drop-up' : ''}`}
          id={listboxId}
          role="listbox"
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="cg-select-option"
              data-active={option.value === value}
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value)
                setIsOpen(false)
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
